export const SITE_URL = "https://deerparkestates.ca";

// Per-route SEO metadata. Keys are exact pathnames; dynamic routes use a matcher.
export const DEFAULT_SEO = {
  title: "Deer Park Estates · Spruce Grove, Alberta",
  description:
    "Deer Park Estates — a premium master-planned estate community in Spruce Grove, Alberta. Explore stages, builder lots, community amenities and the interactive master plan.",
};

export const ROUTE_SEO = {
  "/": {
    title: "Deer Park Estates · Premium Estate Community in Spruce Grove, Alberta",
    description:
      "A premium master-planned community in Spruce Grove, Alberta. Discover estate lots, custom home builders, parks, ponds and an interactive master plan at Deer Park Estates.",
  },
  "/master-plan": {
    title: "Interactive Master Plan · Deer Park Estates",
    description:
      "Explore the Deer Park Estates master plan — stages, parcels, parks and commercial areas on an interactive map of the Spruce Grove community.",
  },
  "/available-land": {
    title: "Available Land & Lots · Deer Park Estates",
    description:
      "Browse available estate lots at Deer Park Estates in Spruce Grove, Alberta. Lot dimensions, frontage, depth and plot plans for registered builders.",
  },
  "/sold-reserved": {
    title: "Committed Lots · Homes Coming · Deer Park Estates",
    description:
      "See which lots are committed to custom home builders across the stages of Deer Park Estates, Spruce Grove, Alberta — and connect with the builder to make one your home.",
  },
  "/stages": {
    title: "Stages & Phases · Deer Park Estates",
    description:
      "Explore the development stages and phases of Deer Park Estates, a master-planned community in Spruce Grove, Alberta.",
  },
  "/builders": {
    title: "Custom Home Builders · Deer Park Estates",
    description:
      "Meet the approved custom home builders of Deer Park Estates in Spruce Grove, Alberta, and view their portfolios.",
  },
  "/commercial": {
    title: "Commercial Centre · Deer Park Estates",
    description:
      "A planned secured commercial centre driving long-term value at Deer Park Estates, Spruce Grove, Alberta.",
  },
  "/apartment": {
    title: "Apartment Project · Deer Park Estates",
    description:
      "A planned multi-family apartment project expanding the housing mix at Deer Park Estates, Spruce Grove, Alberta.",
  },
  "/location": {
    title: "Location & Nearby Amenities · Deer Park Estates",
    description:
      "See where Deer Park Estates sits in Spruce Grove, Alberta, and explore nearby schools, shopping, parks and transit.",
  },
  "/community": {
    title: "Community Overview · Deer Park Estates",
    description:
      "Discover life at Deer Park Estates — local businesses, parks and amenities, and getting around Spruce Grove, Alberta.",
  },
  "/community/local-businesses": {
    title: "Local Businesses · Deer Park Estates Community",
    description:
      "A directory of local businesses serving the Deer Park Estates community in Spruce Grove, Alberta.",
  },
  "/community/parks-and-amenities": {
    title: "Parks & Amenities · Deer Park Estates Community",
    description:
      "Explore the parks, trails, stormwater ponds and green amenities designed for the Deer Park Estates community in Spruce Grove, Alberta.",
  },
  "/community/getting-around": {
    title: "Getting Around · Deer Park Estates Community",
    description:
      "Transit, roads and connectivity around Deer Park Estates in Spruce Grove, Alberta.",
  },
  "/about": {
    title: "About Deer Park Estates",
    description:
      "The story and vision behind Deer Park Estates, a premium master-planned community in Spruce Grove, Alberta.",
  },
  "/history": {
    title: "About Deer Park Estates",
    description:
      "The story and vision behind Deer Park Estates, a premium master-planned community in Spruce Grove, Alberta.",
  },
  "/contact": {
    title: "Contact · Deer Park Estates",
    description:
      "Get in touch with the Deer Park Estates team about estate lots and builder opportunities in Spruce Grove, Alberta.",
  },
};

// Dynamic-route fallbacks (pathname startsWith)
export const DYNAMIC_SEO = [
  {
    prefix: "/stages/",
    title: "Stage Detail · Deer Park Estates",
    description:
      "Detailed view of a development stage at Deer Park Estates, Spruce Grove, Alberta — lots, dimensions and availability.",
  },
  {
    prefix: "/builders/",
    title: "Builder Profile · Deer Park Estates",
    description:
      "Profile and portfolio of a custom home builder at Deer Park Estates, Spruce Grove, Alberta.",
  },
];

// Routes that should not be indexed
export const NOINDEX_PREFIXES = ["/login", "/admin", "/portal"];

export function resolveSeo(pathname) {
  const p = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  if (ROUTE_SEO[p]) return { ...ROUTE_SEO[p], noindex: isNoindex(p) };
  const dyn = DYNAMIC_SEO.find((d) => p.startsWith(d.prefix));
  if (dyn) return { title: dyn.title, description: dyn.description, noindex: isNoindex(p) };
  return { ...DEFAULT_SEO, noindex: isNoindex(p) };
}

export function isNoindex(pathname) {
  return NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));
}

// Human-readable labels for breadcrumb segments
export const SEGMENT_LABELS = {
  "master-plan": "Master Plan",
  "available-land": "Available Land",
  "sold-reserved": "Sold & Reserved",
  stages: "Stages",
  builders: "Builders",
  commercial: "Commercial",
  apartment: "Apartment",
  location: "Location",
  community: "Community",
  "local-businesses": "Local Businesses",
  "parks-and-amenities": "Parks & Amenities",
  "getting-around": "Getting Around",
  about: "About",
  contact: "Contact",
};

const titleCase = (s) =>
  s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Returns [{ name, path }] starting from Home; empty extra crumbs on root
export function buildBreadcrumbs(pathname) {
  const p = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
  const crumbs = [{ name: "Home", path: "/" }];
  if (p === "/") return crumbs;
  const segs = p.split("/").filter(Boolean);
  let acc = "";
  segs.forEach((seg) => {
    acc += `/${seg}`;
    crumbs.push({ name: SEGMENT_LABELS[seg] || titleCase(seg), path: acc });
  });
  return crumbs;
}

// Real-estate agent / seller organization (Broadview Developments Inc.)
export const ORG_CONTACT = {
  legalName: "Broadview Developments Inc.",
  telephone: "+1-780-445-8188",
  faxNumber: "+1-780-483-1424",
  email: "franz.leung@gmail.com",
  streetAddress: "13912 - 85 Ave.",
  addressLocality: "Edmonton",
  addressRegion: "AB",
  postalCode: "T5R 3Y8",
  addressCountry: "CA",
};

// FAQ content (shown on /contact + emitted as FAQPage structured data)
export const FAQ_ITEMS = [
  {
    q: "Who can purchase land at Deer Park Estates?",
    a: "Lots at Deer Park Estates are available to registered, approved home builders only — they are not for sale to private individuals. Builders can request access through our land team or the Builders Login.",
  },
  {
    q: "Where is Deer Park Estates located?",
    a: "Deer Park Estates is a master-planned community in northwest Spruce Grove, Alberta — roughly 220 acres of tree-lined streets, two ponds, preserved natural areas, parks and trails, with easy access to the Yellowhead Highway.",
  },
  {
    q: "Which stages are currently available?",
    a: "The community is delivered across 12 stages. Stages 1–6 are built out, lots in Stages 7 and 9 are committed by builders, Stage 8 is now selling, and later stages are in development. See the Stages page for current availability.",
  },
  {
    q: "Are lot prices shown on the website?",
    a: "No. Exact parcel pricing is shared privately with registered builders on enquiry. Public pages show lot dimensions, frontage, depth and plot plans, but not prices.",
  },
  {
    q: "What details are provided for each lot?",
    a: "Each lot listing includes area, frontage and depth, and — where available — a downloadable plot plan PDF. Servicing and zoning details are provided to builders on request.",
  },
  {
    q: "How do I become an approved builder or get access?",
    a: "Contact our land team through the enquiry form on this page, or sign in via the Builders Login to access confidential documents and the investor portal.",
  },
  {
    q: "What amenities does the community offer?",
    a: "Deer Park Estates features stormwater ponds, walking trails, parks and playgrounds, a local shopping plaza with everyday businesses, and public transit including Spruce Grove Route 560 to Edmonton.",
  },
];

export function faqPageLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

